import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NsKey } from "enums/NsKey";
import { Dropdown, DropdownItem } from "components/forms/Dropdown";
import { PricingSideMenuCard } from "features/subscription-process/pricing-process/components/common/PricingSideMenuCard";
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { BorrowerCommissionCardItem } from "./BorrowerCommissionCardItem";
import { CustomContractFeesDto, FeesReferentialDto } from "api/pricing";

type Props = {
  updateDelay?: number;
}

const LISSAGE_OPTIONS: DropdownItem<string>[] = [
  { key: 'SANS_LISSAGE', label: 'Sans lissage' },
  { key: 'LISSAGE_1ER_ANNEE', label: 'Lissage 1ère année' },
];

export const BorrowerCommissionCard: React.FC<Props> = ({
  updateDelay = 1000
}) => {
  const { t } = useTranslation(NsKey.Pricing);
  const { adjustmentsConfig, borrowersCommissions, borrowerCommissionValue, fees } = useMainBorrowerPricingState();
  const { updateAdjusmentsConfig, onAdjustments, syncGlobalFeesToFunnels } = useMainBorrowerPricingActions();

  // Refs pour éviter les boucles infinies
  const adjustmentsConfigRef = useRef(adjustmentsConfig);
  const updateAdjusmentsConfigRef = useRef(updateAdjusmentsConfig);

  // Mettre à jour les refs quand les valeurs changent
  useEffect(() => {
    adjustmentsConfigRef.current = adjustmentsConfig;
  }, [adjustmentsConfig]);

  useEffect(() => {
    updateAdjusmentsConfigRef.current = updateAdjusmentsConfig;
  }, [updateAdjusmentsConfig]);

  const items = useMemo(() => {
    if (!borrowersCommissions) {
      return [];
    }
    return borrowersCommissions.map((item) => ({
      key: item.id,
      label: item.label,
    }));
  }, [borrowersCommissions]);

  const defaultItem = useMemo(() => {
    if (borrowerCommissionValue) {
      const found = items.find(item => item.key === borrowerCommissionValue);
      if (found) return found;
    }
    return undefined;
  }, [items, borrowerCommissionValue]);

  const [distribCom, setDistribCom] = useState(defaultItem);

  // Mettre à jour distribCom quand defaultItem change (données asynchrones)
  useEffect(() => {
    if (defaultItem) {
      setDistribCom(defaultItem);
    }
  }, [defaultItem]);

  const [modeLissage, setModeLissage] = useState<DropdownItem<string>>(
    LISSAGE_OPTIONS.find(o => o.key === adjustmentsConfig.modeLissageFraisIntermediation) ?? LISSAGE_OPTIONS[1] // LISSAGE_1ER_ANNEE by default
  );

  const [feeValues, setFeeValues] = useState<Record<string, number>>({});
  const isFeesInitializedRef = useRef(false);

  // Ref pour feeValues
  const feeValuesRef = useRef(feeValues);
  useEffect(() => {
    feeValuesRef.current = feeValues;
  }, [feeValues]);

  // Helper pour convertir feeValues en CustomContractFeesDto[]
  const buildFeesArray = useCallback((values: Record<string, number>): FeesReferentialDto[] => {
    return Object.entries(values).map(([code, value]) => ({
      code,
      value,
    }));
  }, []);

  // Initialiser feeValues avec les valeurs initiales des fees
  useEffect(() => {
    if (fees && fees.length > 0) {
      const initialFeeValues: Record<string, number> = {};
      fees.forEach((fee) => {
        initialFeeValues[fee.code] = fee.value;
      });
      setFeeValues(initialFeeValues);
    }
  }, [fees]);

  // Mise à jour de la commission distributeur (avec les fees actuels)
  useEffect(() => {
    if (distribCom && distribCom.key !== adjustmentsConfigRef.current.borrowerCommission) {
      updateAdjusmentsConfigRef.current({
        ...adjustmentsConfigRef.current,
        borrowerCommission: distribCom.key,
        fees: buildFeesArray(feeValuesRef.current),
      });
    }
  }, [distribCom, buildFeesArray]);

  // Mise à jour des fees : propagation temps réel + appel API individuel par funnel via middleware
  useEffect(() => {
    if (Object.keys(feeValues).length === 0) return;

    // Premier chargement : juste stocker les fees dans adjustmentsConfig, pas de sync/custom-global
    if (!isFeesInitializedRef.current) {
      isFeesInitializedRef.current = true;
      updateAdjusmentsConfigRef.current({
        ...adjustmentsConfigRef.current,
        fees: buildFeesArray(feeValues),
      });
      return;
    }

    // D'abord propager les fees aux funnels non-édités (le reducer compare avec l'ancien adjustmentsConfig)
    syncGlobalFeesToFunnels({ fees: feeValues });

    // Puis mettre à jour adjustmentsConfig.fees pour garder la valeur globale dans le state
    updateAdjusmentsConfigRef.current({
      ...adjustmentsConfigRef.current,
      fees: buildFeesArray(feeValues),
    });
  }, [feeValues, buildFeesArray, syncGlobalFeesToFunnels]);

  // Déclencher onAdjustments uniquement quand la commission distributeur change
  const prevDistribComRef = useRef(distribCom);
  useEffect(() => {
    if (distribCom && prevDistribComRef.current && distribCom.key !== prevDistribComRef.current.key) {
      onAdjustments({ borrowerCommission: distribCom.key });
    }
    prevDistribComRef.current = distribCom;
  }, [distribCom, onAdjustments]);

  // Déclencher onAdjustments quand le mode de lissage change
  const prevModeLissageRef = useRef(modeLissage);
  useEffect(() => {
    if (modeLissage && prevModeLissageRef.current && modeLissage.key !== prevModeLissageRef.current.key) {
      updateAdjusmentsConfigRef.current({
        ...adjustmentsConfigRef.current,
        modeLissageFraisIntermediation: modeLissage.key,
      });
      onAdjustments({ modeLissageFraisIntermediation: modeLissage.key });
    }
    prevModeLissageRef.current = modeLissage;
  }, [modeLissage, onAdjustments]);

  const handleFeeChange = useCallback((feeCode: string) => {
    return (setValue: React.SetStateAction<number>) => {
      setFeeValues((prev) => {
        const currentValue = prev[feeCode] || 0;
        const newValue = typeof setValue === 'function'
          ? setValue(currentValue)
          : setValue;

        return {
          ...prev,
          [feeCode]: newValue
        };
      });
    };
  }, []);

  return (
    <PricingSideMenuCard
      title={t("PRICING.SIDE_MENU.FEES_AND_COMMISSION")}
      icon="filter-vertical"
    >
      <div className="flex flex-row justify-between xl:justify-normal xl:flex-col gap-4">
        {fees && fees.map((fee) => (
          <BorrowerCommissionCardItem
            disabledUnit
            key={`fee-${fee.code}`}
            title={fee.label}
            subtitle={fee.parameters && fee.parameters.unit ? `en ${fee.parameters.unit}` : undefined}
            value={feeValues[fee.code] ?? fee.value}
            onChange={handleFeeChange(fee.code)}
            parameters={fee.parameters}
          />
        ))}
        <Dropdown
          label="Mode de lissage"
          name="modeLissage"
          onSelect={(item) => setModeLissage(item as DropdownItem<string>)}
          selectedItem={modeLissage}
          items={LISSAGE_OPTIONS}
        />
        <Dropdown
          label="Code statistique"
          name="LIN"
          onSelect={(item) => setDistribCom(item as DropdownItem<string>)}
          selectedItem={distribCom ?? null}
          items={items}
        />
      </div>
    </PricingSideMenuCard>
  );
};
